import { useState, useMemo } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useNotesData } from "@/hooks/useNotesData";
import NoteEditor from "@/components/notes/NoteEditor";
import { LinkEventDialog, ShareNoteDialog, DeleteNoteDialog } from "@/components/notes/NoteDialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, FileText, Trash2, Link2, CalendarDays, Share2, X, Eye } from "lucide-react";
import { listEvents, shareNote, unshareNote, listTeam } from "@/lib/api";
import { useEffect } from "react";

export default function NotesPage() {
  const { role } = useOutletContext();
  const { notes, loading, handleCreate, handleUpdate, handleDelete, refetch } = useNotesData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [search, setSearch] = useState("");
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState([]);
  const [eventSearch, setEventSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sharePermission, setSharePermission] = useState("view");

  useEffect(() => {
    listEvents().then(res => setEvents(res.data)).catch(() => {});
    if (role === "owner") {
      listTeam().then(res => setTeam(res.data)).catch(() => {});
    }
  }, [role]);

  // Auto-select note from query param (e.g. /notes?select=note_xxx)
  useEffect(() => {
    const selectId = searchParams.get("select");
    if (selectId && notes.length > 0) {
      const found = notes.find(n => n.note_id === selectId);
      if (found) {
        setActiveNoteId(selectId);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, notes, setSearchParams]);

  const activeNote = useMemo(
    () => notes.find(n => n.note_id === activeNoteId),
    [notes, activeNoteId]
  );

  const filteredNotes = useMemo(() => {
    if (!search) return notes;
    const q = search.toLowerCase();
    return notes.filter(n =>
      n.title?.toLowerCase().includes(q) ||
      n.linked_event_name?.toLowerCase().includes(q)
    );
  }, [notes, search]);

  const filteredEvents = useMemo(() => {
    if (!eventSearch) return events.slice(0, 20);
    const q = eventSearch.toLowerCase();
    return events.filter(e => e.name?.toLowerCase().includes(q)).slice(0, 20);
  }, [events, eventSearch]);

  // Team members not already shared with
  const shareableUsers = useMemo(() => {
    if (!activeNote) return [];
    const sharedIds = (activeNote.shared_with || []).map(s => s.user_id);
    return team.filter(u => u.user_id !== activeNote.owner_id && !sharedIds.includes(u.user_id));
  }, [team, activeNote]);

  const onCreateNote = async () => {
    try {
      const note = await handleCreate({ title: "Untitled", content: {} });
      setActiveNoteId(note.note_id);
    } catch {
      toast.error("Failed to create note");
    }
  };

  const onDeleteNote = async (noteId) => {
    try {
      await handleDelete(noteId);
      if (activeNoteId === noteId) setActiveNoteId(null);
      setDeleteConfirm(null);
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const onSaveUpdate = async (noteId, data) => {
    try { await handleUpdate(noteId, data); } catch (err) { toast.error("Auto-save failed — retrying on next change"); }
  };

  const onLinkEvent = async (event) => {
    if (!activeNote) return;
    try {
      await handleUpdate(activeNote.note_id, {
        linked_event_id: event.event_id,
        linked_event_name: event.name,
      });
      setLinkDialogOpen(false);
      setEventSearch("");
      toast.success(`Linked to "${event.name}"`);
    } catch {
      toast.error("Failed to link event");
    }
  };

  const onUnlinkEvent = async () => {
    if (!activeNote) return;
    try {
      await handleUpdate(activeNote.note_id, { linked_event_id: null, linked_event_name: null });
      toast.success("Event unlinked");
    } catch {
      toast.error("Failed to unlink");
    }
  };

  const onShareWithUser = async (targetUserId) => {
    if (!activeNote) return;
    try {
      await shareNote(activeNote.note_id, { user_id: targetUserId, permission: sharePermission });
      await refetch();
      toast.success("Note shared");
    } catch {
      toast.error("Failed to share");
    }
  };

  const onRemoveShare = async (targetUserId) => {
    if (!activeNote) return;
    try {
      await unshareNote(activeNote.note_id, targetUserId);
      await refetch();
      toast.success("Access removed");
    } catch {
      toast.error("Failed to remove access");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const isReadOnly = activeNote?._permission === "view";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="notes-page" className="flex flex-col md:flex-row md:h-[calc(100vh-80px)]">
      {/* Left Panel — stacks above the editor on phones */}
      <div className="w-full md:w-72 border-b md:border-b-0 md:border-r border-border flex flex-col shrink-0" data-testid="notes-list-panel">
        <div className="p-3 border-b border-border space-y-2">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Notes</h1>
            <Button data-testid="create-note-btn" variant="ghost" size="icon" className="h-7 w-7" onClick={onCreateNote}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              data-testid="notes-search"
              placeholder="Search notes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm bg-secondary/30 border-border"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto max-h-56 md:max-h-none">
          {filteredNotes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <FileText className="w-8 h-8 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">{search ? "No matching notes" : "No notes yet"}</p>
              {!search && (
                <Button variant="ghost" size="sm" className="mt-2 text-xs text-primary" onClick={onCreateNote}>
                  Create your first note
                </Button>
              )}
            </div>
          )}
          {filteredNotes.map(note => (
            <button
              key={note.note_id}
              data-testid={`note-item-${note.note_id}`}
              className={`w-full text-left px-3 py-2.5 border-b border-border/50 transition-colors hover:bg-secondary/30 ${
                activeNoteId === note.note_id ? "bg-secondary/50 border-l-2 border-l-primary" : ""
              }`}
              onClick={() => setActiveNoteId(note.note_id)}
            >
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-sm truncate flex-1">{note.title || "Untitled"}</span>
                {note._shared && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 shrink-0 border-blue-500/40 text-blue-400">
                    shared
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{formatDate(note.updated_at)}</span>
                {note.linked_event_name && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 truncate max-w-[120px]">
                    <CalendarDays className="w-2.5 h-2.5 mr-0.5 shrink-0" />
                    {note.linked_event_name}
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeNote ? (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div className="flex items-center gap-2 min-w-0">
                {activeNote.linked_event_name ? (
                  <Badge variant="secondary" className="text-xs gap-1 shrink-0 cursor-pointer hover:bg-secondary/80" onClick={!isReadOnly ? onUnlinkEvent : undefined} data-testid="unlink-event-badge">
                    <Link2 className="w-3 h-3" />
                    {activeNote.linked_event_name}
                    {!isReadOnly && <X className="w-3 h-3 ml-0.5" />}
                  </Badge>
                ) : !isReadOnly ? (
                  <Button data-testid="link-event-btn" variant="ghost" size="sm" className="text-xs text-muted-foreground h-7 gap-1" onClick={() => setLinkDialogOpen(true)}>
                    <Link2 className="w-3 h-3" /> Link to event
                  </Button>
                ) : null}
                {isReadOnly && (
                  <Badge variant="outline" className="text-xs gap-1 border-amber-500/40 text-amber-400">
                    <Eye className="w-3 h-3" /> View only
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground mr-1">{formatDate(activeNote.updated_at)}</span>
                {!activeNote._shared && (
                  <Button data-testid="share-note-btn" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => setShareDialogOpen(true)} title="Share">
                    <Share2 className="w-3.5 h-3.5" />
                  </Button>
                )}
                {!activeNote._shared && (
                  <Button data-testid="delete-note-btn" variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => setDeleteConfirm(activeNote.note_id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <NoteEditor key={activeNote.note_id} note={activeNote} onUpdate={onSaveUpdate} readOnly={isReadOnly} />
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <FileText className="w-12 h-12 text-muted-foreground/20 mb-4" />
            <p className="text-muted-foreground text-sm">Select a note or create a new one</p>
          </div>
        )}
      </div>

      <LinkEventDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        eventSearch={eventSearch}
        setEventSearch={setEventSearch}
        filteredEvents={filteredEvents}
        onLinkEvent={onLinkEvent}
      />

      <ShareNoteDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        activeNote={activeNote}
        shareableUsers={shareableUsers}
        sharePermission={sharePermission}
        setSharePermission={setSharePermission}
        onShareWithUser={onShareWithUser}
        onRemoveShare={onRemoveShare}
      />

      <DeleteNoteDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => onDeleteNote(deleteConfirm)}
      />
    </div>
  );
}
